import React from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TablePagination from "@mui/material/TablePagination";
import MarketingContacts from "./MarketingContacts";

export default function MarketingAudienceStep(props) {
  const {
    setFlowStep,
    selectedCategoryId,
    categories,
    setSelectedCategoryId,
    setSelectedCategory,
    setUnsentContacts,
    setSentContacts,
    setNoEmailContacts,
    setSelectedContacts,
    setPage,
    loadContactsForCategory,
    newCategoryName,
    setNewCategoryName,
    handleCreateCategory,
    creatingCategory,
    handleDeleteCategory,
    selectedCategoryStats,
    selectedCategory,
    searchTerm,
    setSearchTerm,
    analyticsFilter,
    setAnalyticsFilter,
    audienceSubjectFilter,
    setAudienceSubjectFilter,
    audienceLinkFilter,
    setAudienceLinkFilter,
    audienceClickFilterOptions,
    audienceClickFilterBusy,
    audienceClickFilterError,
    showNoEmail,
    statusFilter,
    setStatusFilter,
    sentContacts,
    unsentContacts,
    sentDateFilter,
    setSentDateFilter,
    sentSignupFilter,
    setSentSignupFilter,
    noEmailContacts,
    setShowNoEmail,
    selectCount,
    setSelectCount,
    handleSelectCount,
    handleSelectAll,
    handleDeselectAll,
    selectedCount,
    visibleContacts,
    page,
    rowsPerPage,
    setRowsPerPage,
    selectedContacts,
    hasValidEmail,
    toggleContact,
    startInlineEdit,
    editRow,
    editField,
    editValue,
    setEditValue,
    saveInlineEdit,
    openNotesDialog,
    openIntakeModal,
    handleDeleteContact,
    onOpenUserClicks,
  } = props;

  const getNameParts = (contact) => {
    const first = String(contact?.firstName || "").trim();
    const last = String(contact?.lastName || "").trim();
    if (first || last) return { first, last };

    const full = String(contact?.name || contact?.username || "").trim();
    if (!full) return { first: "", last: "" };

    const parts = full.split(/\s+/).filter(Boolean);
    return { first: parts[0] || "", last: parts.slice(1).join(" ") };
  };
  const allWithEmailCount = React.useMemo(() => {
    const seen = new Set();
    [...(unsentContacts || []), ...(sentContacts || [])].forEach((c) => {
      const email = String(c?.email || "").trim().toLowerCase();
      if (email) seen.add(email);
    });
    return seen.size;
  }, [unsentContacts, sentContacts]);
  const withEmailStatusCount = statusFilter === "sent"
    ? sentContacts.length
    : statusFilter === "unsent"
      ? unsentContacts.length
      : allWithEmailCount;

  return (
    <div className="crd space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-[#333999]">Audience</div>
          <div className="text-xs text-slate-500 mt-1">Pick a category and select recipients.</div>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => setFlowStep(1)}
          disabled={!selectedCategoryId}
          title={!selectedCategoryId ? "Select a category first" : "Continue to content"}
        >
          Continue
        </button>
      </div>

      <div className="section-divider" />

      <div className="space-y-3">
        <label className="section-label">Select Category</label>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <select
            className="w-full sm:flex-1 rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
            value={selectedCategoryId}
            onChange={(e) => {
              const id = e.target.value;
              const cat = categories.find((c) => c.id === id);

              if (!cat) {
                setSelectedCategoryId("");
                setSelectedCategory("");
                setUnsentContacts([]);
                setSentContacts([]);
                setNoEmailContacts([]);
                setAudienceSubjectFilter("all");
                setAudienceLinkFilter("all");
                setSelectedContacts({});
                setPage(0);
                return;
              }
              loadContactsForCategory(cat);
            }}
          >
            <option value="">-- choose a category --</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <div className="flex w-full sm:w-auto items-center gap-2">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="w-full sm:w-56 rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
              placeholder="New category name"
            />
            <button
              type="button"
              onClick={handleCreateCategory}
              disabled={creatingCategory}
              className={`btn-primary btn-sm ${creatingCategory ? "opacity-60 cursor-not-allowed" : ""}`}
              title="Create a new category"
            >
              + Add
            </button>
          </div>

          <button
            type="button"
            onClick={handleDeleteCategory}
            disabled={!selectedCategoryId}
            className={`btn-danger btn-pill btn-sm ${!selectedCategoryId ? "opacity-60 cursor-not-allowed" : ""}`}
            title="Delete selected category"
          >
            <DeleteOutlineIcon fontSize="small" />
            Delete
          </button>
        </div>

        {selectedCategoryStats ? (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="ln-stat-card px-4 py-3 rounded-2xl">
              <div className="ln-stat-label">Contacts</div>
              <div className="text-2xl font-extrabold">{selectedCategoryStats.contactCount}</div>
            </div>
            <div className="ln-stat-card px-4 py-3 rounded-2xl">
              <div className="ln-stat-label">With Email</div>
              <div className="text-2xl font-extrabold">{selectedCategoryStats.contactEmailCount}</div>
            </div>
            <div className="ln-stat-card px-4 py-3 rounded-2xl">
              <div className="ln-stat-label">Total Signups</div>
              <div className="text-2xl font-extrabold">
                {selectedCategoryStats.totalSignups}{" "}
                <span className="text-xs text-slate-500 font-bold">
                  ({selectedCategoryStats.contactCount ? ((selectedCategoryStats.totalSignups * 100) / selectedCategoryStats.contactCount).toFixed(2) : "0.00"}%)
                </span>
              </div>
            </div>
          </div>
        ) : null}

        {selectedCategoryId ? (
          <div className="mt-5">
            <div className="section-divider mb-4" />
            <MarketingContacts selectedCategory={{ id: selectedCategoryId, name: selectedCategory }} />
          </div>
        ) : (
          <div className="mt-4 text-sm text-slate-500">Select a category above to manage/upload contacts.</div>
        )}
      </div>

      <div className="section-divider" />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div>
          <label className="section-label">Search Contacts</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
            placeholder="Search by email, company, name, phone, address, or website"
          />
        </div>

        <div>
          <label className="section-label">Analytics Filter</label>
          <select
            className="w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
            value={analyticsFilter}
            onChange={(e) => {
              setAnalyticsFilter(e.target.value);
              setPage(0);
            }}
          >
            <option value="all">All</option>
            <option value="opened">Opened</option>
            <option value="clicked_explorer">Clicked Explorer</option>
            <option value="clicked_youtube">Clicked YouTube</option>
            <option value="clicked_meeting">Clicked Meeting</option>
            <option value="clicked_unsubscribe">Clicked Unsubscribe</option>
            <option value="delivered">Delivered</option>
            <option value="processed">Processed</option>
            <option value="bounced">Bounced</option>
          </select>
        </div>

        <div>
          <label className="section-label">Filter by Subject</label>
          <select
            className={`w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 ${showNoEmail ? "opacity-60 cursor-not-allowed" : ""}`}
            value={audienceSubjectFilter}
            disabled={showNoEmail || !selectedCategoryId}
            onChange={(e) => {
              setAudienceSubjectFilter(e.target.value);
              setPage(0);
            }}
          >
            <option value="all">All Subjects</option>
            {(audienceClickFilterOptions.subjects || []).map((s, i) => (
              <option key={`${s}-${i}`} value={String(s).toLowerCase()}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="section-label">Filter by Clicked Link</label>
          <select
            className={`w-full rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 ${showNoEmail ? "opacity-60 cursor-not-allowed" : ""}`}
            value={audienceLinkFilter}
            disabled={showNoEmail || !selectedCategoryId}
            onChange={(e) => {
              setAudienceLinkFilter(e.target.value);
              setPage(0);
            }}
          >
            <option value="all">All Links</option>
            {(audienceClickFilterOptions.links || []).map((u, i) => (
              <option key={`${u}-${i}`} value={String(u).toLowerCase()}>
                {u}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCategoryId && !showNoEmail ? (
        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
          <span>
            Subject and link filters use tracked click history for the selected category and match the exact clicked URL.
          </span>
          {audienceClickFilterBusy ? <span className="font-semibold text-indigo-700">Loading filter options…</span> : null}
          {audienceClickFilterError ? <span className="text-red-600">{audienceClickFilterError}</span> : null}
        </div>
      ) : null}

      <div className="flex flex-col lg:flex-row gap-6 mt-2">
        <div className="flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Status</div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              className={`w-44 rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 ${showNoEmail ? "opacity-60 cursor-not-allowed" : ""}`}
              disabled={showNoEmail}
              value={statusFilter}
              onChange={(e) => {
                const next = String(e.target.value || "all");
                setStatusFilter(next);
                if (next !== "sent") {
                  setSentDateFilter("");
                  setSentSignupFilter("all");
                }
                setSelectedContacts({});
                setPage(0);
              }}
            >
              <option value="all">All</option>
              <option value="unsent">Unsent</option>
              <option value="sent">Sent</option>
            </select>
            <span className="text-xs text-slate-500">
              {`Showing ${withEmailStatusCount} ${statusFilter === "sent" ? "sent" : statusFilter === "unsent" ? "unsent" : "total"}`}
            </span>
          </div>

          {statusFilter === "sent" && !showNoEmail ? (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Sent Date</label>
                <input
                  type="date"
                  value={sentDateFilter}
                  onChange={(e) => {
                    setSentDateFilter(e.target.value);
                    setPage(0);
                  }}
                  className="w-44 rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                />
                <button
                  type="button"
                  className={`btn-ghost btn-sm ${!sentDateFilter ? "opacity-60 cursor-not-allowed" : ""}`}
                  disabled={!sentDateFilter}
                  onClick={() => {
                    setSentDateFilter("");
                    setPage(0);
                  }}
                >
                  Clear
                </button>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Signup</label>
                <select
                  className="w-44 rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                  value={sentSignupFilter}
                  onChange={(e) => {
                    setSentSignupFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <option value="all">All</option>
                  <option value="signed">Signed up</option>
                  <option value="not_signed">Not signed up</option>
                </select>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Email</div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="ln-toggle-group">
              <button
                type="button"
                className={`ln-toggle-pill ${!showNoEmail ? "ln-toggle-pill--active" : ""}`}
                onClick={() => {
                  setShowNoEmail(false);
                  setSelectedContacts({});
                  setPage(0);
                  if (selectedCategory) {
                    loadContactsForCategory({ id: selectedCategoryId, name: selectedCategory }, { showNoEmail: false });
                  }
                }}
              >
                With Email
              </button>

              <button
                type="button"
                className={`ln-toggle-pill ${showNoEmail ? "ln-toggle-pill--active" : ""}`}
                onClick={() => {
                  setShowNoEmail(true);
                  setAnalyticsFilter("all");
                  setAudienceSubjectFilter("all");
                  setAudienceLinkFilter("all");
                  setStatusFilter("all");
                  setSentDateFilter("");
                  setSentSignupFilter("all");
                  setSelectedContacts({});
                  setPage(0);
                }}
              >
                No Email
              </button>
            </div>
            <span className="text-xs text-slate-500">
              {showNoEmail ? `Showing ${noEmailContacts.length} no-email` : `Showing ${withEmailStatusCount} with email`}
            </span>
          </div>
        </div>
      </div>

      <div className="section-divider" />

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="number"
          value={selectCount}
          onChange={(e) => setSelectCount(e.target.value)}
          className="w-28 rounded-xl border border-slate-300/70 bg-white/90 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
          placeholder="Count"
        />
        <button onClick={handleSelectCount} disabled={showNoEmail} className={`btn-primary btn-sm ${showNoEmail ? "opacity-60 cursor-not-allowed" : ""}`} type="button">
          Select
        </button>
        <button onClick={handleSelectAll} disabled={showNoEmail} className={`btn-primary btn-sm ${showNoEmail ? "opacity-60 cursor-not-allowed" : ""}`} type="button">
          Select All
        </button>
        <button onClick={handleDeselectAll} disabled={showNoEmail} className={`btn-ghost btn-sm ${showNoEmail ? "opacity-60 cursor-not-allowed" : ""}`} type="button">
          Deselect All
        </button>

        <div className="flex-1" />
        <span className="text-xs text-slate-500">
          Selected: <b className="text-slate-800">{selectedCount}</b>
        </span>
      </div>

      <div className="max-h-[520px] overflow-auto rounded-2xl border border-slate-300/70 bg-white/70">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 z-10 bg-gradient-to-r from-slate-50 to-indigo-50 border-b border-slate-200">
            <tr className="text-slate-700">
              <th className="px-3 py-2 text-left font-bold text-[#333999]">Send?</th>
              <th className="px-3 py-2 text-left font-bold text-[#333999]">First Name</th>
              <th className="px-3 py-2 text-left font-bold text-[#333999]">Last Name</th>
              <th className="px-3 py-2 text-left font-bold text-[#333999]">Company</th>
              <th className="px-3 py-2 text-left font-bold text-[#333999]">Email</th>
              <th className="px-3 py-2 text-left font-bold text-[#333999]">Phone</th>
              <th className="px-3 py-2 text-left font-bold text-[#333999]">Website</th>
              <th className="px-3 py-2 text-left font-bold text-[#333999]">Address</th>
              <th className="px-3 py-2 text-left font-bold text-[#333999]">Notes</th>
              <th className="px-3 py-2 text-left font-bold text-[#333999]">{statusFilter === "sent" ? "Engagement" : "Status"}</th>
              <th className="px-3 py-2 text-left font-bold text-[#333999]">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200">
            {(() => {
              const filtered = visibleContacts;
              if (filtered.length === 0) {
                return (
                  <tr>
                    <td colSpan={11} className="px-3 py-6 text-center text-slate-400">
                      No contacts loaded.
                    </td>
                  </tr>
                );
              }

              return filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((c) => {
                const isValidEmailLocal = hasValidEmail(c.email);
                const checked = !!selectedContacts[c.email] && isValidEmailLocal && !showNoEmail;
                const nameParts = getNameParts(c);

                return (
                  <tr key={c.id || c.email || `${c.company || ""}-${c.name || c.username || ""}-${c.phone || ""}`} className="bg-white/80 hover:bg-indigo-50/60 transition-colors">
                    <td className="px-3 py-2 align-top">
                      <input
                        type="checkbox"
                        disabled={showNoEmail || !isValidEmailLocal}
                        checked={checked}
                        onChange={() => toggleContact(c.email)}
                        className="accent-indigo-600"
                      />
                    </td>

                    <td className="px-3 py-2 align-top cursor-pointer" onClick={() => startInlineEdit(c.id, "firstName", nameParts.first)}>
                      {editRow === c.id && editField === "firstName" ? (
                        <input
                          className="w-full rounded-lg border border-slate-300 px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-indigo-300"
                          value={editValue}
                          autoFocus
                          onBlur={saveInlineEdit}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveInlineEdit();
                          }}
                        />
                      ) : (
                        nameParts.first || "-"
                      )}
                    </td>

                    <td className="px-3 py-2 align-top cursor-pointer" onClick={() => startInlineEdit(c.id, "lastName", nameParts.last)}>
                      {editRow === c.id && editField === "lastName" ? (
                        <input
                          className="w-full rounded-lg border border-slate-300 px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-indigo-300"
                          value={editValue}
                          autoFocus
                          onBlur={saveInlineEdit}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveInlineEdit();
                          }}
                        />
                      ) : (
                        nameParts.last || "-"
                      )}
                    </td>

                    <td className="px-3 py-2 align-top cursor-pointer" onClick={() => startInlineEdit(c.id, "company", c.company)}>
                      {editRow === c.id && editField === "company" ? (
                        <input
                          className="w-full rounded-lg border border-slate-300 px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-indigo-300"
                          value={editValue}
                          autoFocus
                          onBlur={saveInlineEdit}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveInlineEdit();
                          }}
                        />
                      ) : (
                        c.company || "-"
                      )}
                    </td>

                    <td className="px-3 py-2 align-top break-all">
                      <div className="flex items-center gap-1 whitespace-nowrap">
                        <span className="cursor-pointer" onClick={() => startInlineEdit(c.id, "email", c.email)}>
                          {c.email || ""}
                        </span>
                        {c.email ? (
                          <ContentCopyIcon
                            fontSize="small"
                            className="opacity-40 hover:opacity-100 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(c.email || "");
                            }}
                          />
                        ) : null}
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-1 whitespace-nowrap">
                        <span className="cursor-pointer" onClick={() => startInlineEdit(c.id, "phone", c.phone)}>
                          {c.phone || "-"}
                        </span>
                        {c.phone ? (
                          <ContentCopyIcon
                            fontSize="small"
                            className="opacity-40 hover:opacity-100 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(c.phone || "");
                            }}
                          />
                        ) : null}
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      {c.website ? (
                        <a
                          href={c.website.startsWith("http") ? c.website : `https://${c.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-700 underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Website
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td className="px-3 py-2 align-top cursor-pointer" onClick={() => startInlineEdit(c.id, "address", c.address)}>
                      {editRow === c.id && editField === "address" ? (
                        <input
                          className="w-full rounded-lg border border-slate-300 px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-indigo-300"
                          value={editValue}
                          autoFocus
                          onBlur={saveInlineEdit}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveInlineEdit();
                          }}
                        />
                      ) : (
                        c.address || "-"
                      )}
                    </td>

                    <td className="px-3 py-2 align-top text-xs">
                      <div className="flex flex-col gap-1">
                        <button type="button" className="text-indigo-700 underline font-semibold text-left" onClick={() => openNotesDialog(c)}>
                          Notes ({Array.isArray(c.notes) ? c.notes.length : 0})
                        </button>
                        <button type="button" className="text-indigo-700 underline font-semibold text-left" onClick={() => openIntakeModal(c.email)}>
                          Open Intake Form
                        </button>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top text-xs">
                      {!isValidEmailLocal ? (
                        <span className="badge-soft-amber">{c.email ? "Invalid email" : "No email"}</span>
                      ) : statusFilter === "sent" || (statusFilter === "all" && !!c?.emailSent) ? (
                        <div className="text-[11px]">
                          <span className="badge-soft-green">Sent</span>
                          <button
                            type="button"
                            className="text-indigo-700 underline font-semibold mt-1 block"
                            onClick={() => onOpenUserClicks?.(c.email || "")}
                          >
                            User Clicks
                          </button>
                        </div>
                      ) : (
                        <span className="badge-soft-green">Ready</span>
                      )}
                    </td>

                    <td className="px-3 py-2 align-top">
                      <button
                        type="button"
                        onClick={() => handleDeleteContact(c)}
                        className="text-red-600 hover:text-red-700 font-semibold inline-flex items-center gap-1"
                        title="Delete contact"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                        <span className="text-xs">Delete</span>
                      </button>
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <TablePagination
          component="div"
          count={visibleContacts.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Rows per page"
          rowsPerPageOptions={[10, 25, 50]}
        />

        <button type="button" className="btn-secondary" onClick={() => setFlowStep(1)} disabled={!selectedCategoryId}>
          Next: Content →
        </button>
      </div>
    </div>
  );
}
